import {useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Device} from '../../../../../../../backend/interfaces/device';
import {UserPublicInfo} from '../../../../../../../backend/responses/user-public-info';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {AuxiliaryDataCacheContext} from '../../../../../../../contexts/auxiliary-data-cache/context';
import {AuxiliaryDataCacheContextType} from '../../../../../../../contexts/auxiliary-data-cache/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {DataCacheManager} from '../../../../../../../managers/data-cache';
import {DevicesRepository} from '../../../../../../../repositories/devices';
import {UsersRepository} from '../../../../../../../repositories/users';

// Columns IDs
enum ColumnIdentifier {
  Identifier = 'identifier',
  Type = 'type',
  State = 'state',
  User = 'user',
}

export default function EntityDevicesSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Get context
  const auxiliaryDataCacheContext: AuxiliaryDataCacheContextType =
    useContext(AuxiliaryDataCacheContext);

  // Translations
  const {t} = useTranslation();

  // Fetch devices
  const handleGetDevices = async (limit: number, skip: number, search?: string) => {
    // Check entity id
    if (!id) {
      // Error
      throw new Error('Empty entity id.');
    }

    // Load devices
    const result: {totalCount: number; data: Array<Device>} =
      await DevicesRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        id,
        undefined,
        false,
        search
      );

    // Process data
    const processedData: PaginatedTableData<Device> = result.data.map((obj) => {
      // Set identifier
      // Note: The serial number is always the identification of any type of device.
      // However, some legacy devices may not have a serial number at the moment, so
      // we need to show the mac address in those cases.
      const identifier: string = obj.serialNumber || obj.macAddress || '';

      // Get the user info
      const userInfo = obj.userId ? getUserInfo(obj.userId) : undefined;

      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        toLink:
          obj.userSignInType === 'code'
            ? ''
            : `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Devices}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Identifier,
            value: identifier,
          },
          {
            columnId: ColumnIdentifier.User,
            value: userInfo?.name || undefined,
            avatarUrl: userInfo?.picture || undefined,
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.entities.edit.devices.state_value_true)
              : t(translations.entities.edit.devices.state_value_false),
          },
          {
            columnId: ColumnIdentifier.Type,
            value: t(translations.device_types[obj.type]) || '',
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<Device>({
    fetchData: handleGetDevices,
  });

  // Fetch user info
  const fetchUserInfo = async (userId: string) => {
    let updateContext: boolean = false;

    try {
      // User is being fetched
      DataCacheManager.instance.addPendingUser(userId);

      // Fetch user info
      const user: UserPublicInfo = await UsersRepository.instance.getInfoById(userId);

      if (user) {
        // Save it on cache
        DataCacheManager.instance.addUser(userId, user);

        // Update
        updateContext = true;
      } else {
        // Error
        throw new Error('Empty user.');
      }
    } catch (error) {
      // Log
      console.debug(`🔴 Could not get user ${userId}.`, error);

      // Save it on cache to not fetch again
      DataCacheManager.instance.addUserWithError(userId);
    } finally {
      // User was fetched
      DataCacheManager.instance.removePendingUser(userId);

      // Update context with users
      if (updateContext) {
        auxiliaryDataCacheContext.setUsers(DataCacheManager.instance.users);
      }
    }
  };

  // Get user info
  const getUserInfo = (userId: string): UserPublicInfo | undefined => {
    if (DataCacheManager.instance.users[userId]) {
      // Return user info
      return DataCacheManager.instance.users[userId];
    } else {
      if (!DataCacheManager.instance.usersWithError[userId]) {
        if (!DataCacheManager.instance.pendingUsers[userId]) {
          // Fetch user info but don't wait for it
          fetchUserInfo(userId);
        } else {
          // Log
          console.debug("⌛ User already being fetched. Let's wait for id:", userId);
        }
      } else {
        // Log
        console.debug(
          '🔴 Could not get the user because an error occurred previously with id:',
          userId
        );
      }
    }
  };

  // Update the data when the users in the auxiliary cache were updated
  useEffect(() => {
    for (let i = 0; i < data.length; i++) {
      if (
        data[i].item.userId &&
        !data[i].columns.find((col) => col.columnId === ColumnIdentifier.User)?.value
      ) {
        const userInfo = getUserInfo(data[i].item.userId as string);

        if (userInfo) {
          handleUpdateItem(data[i].id, [
            {
              updatedColumnId: ColumnIdentifier.User,
              updatedColumnValue: userInfo.name,
              updatedColumnAvatarUrl: userInfo.picture,
            },
          ]);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxiliaryDataCacheContext.state]);

  return (
    <ContentPage>
      <PageHeader
        showSearch={true}
        searchLabel={t(translations.entities.edit.devices.search_input_placeholder)}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
        disableSearch={isLoading}
      />
      <PaginatedTable
        emptyTableMessage={t(translations.entities.edit.devices.no_devices)}
        columns={[
          {
            columnId: ColumnIdentifier.Identifier,
            title: t(translations.entities.edit.devices.identifier_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.Type,
            title: t(translations.entities.edit.devices.type_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.User,
            title: t(translations.entities.edit.devices.user_header),
            flexGrow: 1,
            chip: true,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.entities.edit.devices.state_header),
            flexGrow: 1,
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
    </ContentPage>
  );
}
